// Витрина миниатюр (макет «Витрина · миниатюры»): миры, масштаб, работы, цены, вопросы.
import type { Catalog, CatalogRace } from "../../api/types";
import { useCatalog } from "../../app/queries";
import { SiteFooter } from "../../site/SiteFooter";
import { SiteHeader } from "../../site/SiteHeader";
import { caseImage, caseImageAlt } from "../../site/cases";
import { casePriceLines, formatRub, priceOf, pricedCases } from "../../site/prices";
import { HUMAN_HEIGHT_MM, raceHeightMm } from "../../site/scale";
import { FAQ_PATH, MINI_FAQ, MINI_FAQ_PATH } from "../../site/faq";
import {
  FinalCall,
  Guarantee,
  HowItWorks,
  Questions,
  ShowcaseSection,
  StartButton,
} from "./ShowcaseParts";
import styles from "./Showcase.module.css";
import { SIZES_FULL, SIZES_HALF, SIZES_THIRD, picture } from "../../site/pictures";

// Строка под заголовком витрины. «В фэнтези — раса и класс, в остальных мирах — типажи» убрана:
// покупателю непонятно (Иван, 01.11.2026).
function worldNote(catalog: Catalog | undefined): string {
  const worlds = catalog?.miniature.worlds ?? [];
  return worlds.length <= 1 ? "Выбери расу и класс — или опиши персонажа сам." : "";
}

function worldsTitle(count: number): string {
  if (count <= 1) return "Мир";
  return `${count} ${count < 5 ? "мира" : "миров"}`;
}

// Расы для сравнения роста: человек, эльф, дварф, полурослик — что есть в каталоге.
const SCALE_RACES = ["human", "elf", "mountain_dwarf", "halfling"];

function scaleRaces(catalog: Catalog | undefined): CatalogRace[] {
  const races = catalog?.miniature.worlds.find((world) => world.pick === "race_class")?.races ?? [];
  return SCALE_RACES.map((raceId) => races.find((race) => race.id === raceId)).filter(
    (race): race is CatalogRace => race !== undefined,
  );
}

function capitalized(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const PRICE_ROWS = [
  { product: "print_grey", title: "Серая", text: "одноцветная, под покраску" },
  { product: "file", title: "3D-модель файлом", text: "для своей печати, только на сайте" },
];

// «Работы» (макет, версия 34): примеры персонажей из каталога, лица сгенерированы.
const WORKS = [
  { title: "Эльф · следопыт", world: "фэнтези", image: "/showcase/views_front.jpg" },
  { title: "Горный дварф · воин", world: "фэнтези", image: "/catalog/race_mountain_dwarf.jpg" },
  { title: "Тифлинг · колдун", world: "фэнтези", image: "/catalog/race_tiefling.jpg" },
  { title: "Полурослик · плут", world: "фэнтези", image: "/catalog/race_halfling.jpg" },
];

// «Три вида, выбираешь при заказе.» — по числу видов с ценой.
const KIND_COUNT_WORDS: Record<number, string> = { 2: "Два вида", 3: "Три вида" };

function caseKindsText(count: number): string {
  const kinds = KIND_COUNT_WORDS[count];
  return kinds === undefined ? "Выбираешь при заказе." : `${kinds}, выбираешь при заказе.`;
}

export function ShowcaseMini() {
  const { data: catalog } = useCatalog();
  const worlds = catalog?.miniature.worlds ?? [];
  const cases = pricedCases(catalog, "miniature");
  const casePrices = casePriceLines(cases);
  // У всех видов одна цена — она стоит в строке «Кейс для миниатюры»; разные — строками ниже.
  const singleCasePrice = casePrices.length === 1 ? (casePrices[0]?.price ?? null) : null;
  const prices = PRICE_ROWS.map((row) => ({
    ...row,
    price: catalog ? priceOf(catalog, "miniature", row.product, "mini_40") : null,
  })).filter((row) => row.price !== null);
  return (
    <div className={`${styles.page} mini`}>
      <SiteHeader />
      <main className={styles.main}>
        <section className={styles.hero}>
          <p className="eyebrow" style={{ color: "var(--mini)" }}>
            Миниатюра для игры
          </p>
          <h1 className="h1">Ты — в своей партии</h1>
          <p className="text muted">
            Миниатюра с твоим лицом в масштабе обычных игровых фигурок.{worldNote(catalog) !== "" ? ` ${worldNote(catalog)}` : ""}
          </p>
          <div className={styles.views}>
            <img {...picture("/showcase/views_front.jpg", SIZES_THIRD, "lead")} alt="Лучница спереди" />
            <img {...picture("/showcase/views_side.jpg", SIZES_THIRD, "eager")} alt="Лучница сбоку" />
            <img {...picture("/showcase/views_back.jpg", SIZES_THIRD, "eager")} alt="Лучница сзади" />
          </div>
          <p className="text mono muted">Эльф · следопыт · пример работы, лицо сгенерировано</p>
          <StartButton direction="miniature" />
          <p className={`text small muted ${styles.centered}`}>Эскиз — бесплатно</p>
        </section>

        {worlds.length > 0 && (
          <ShowcaseSection title={worldsTitle(worlds.length)}>
            <div className={styles.covers}>
              {worlds.map((world) => (
                <figure key={world.id} className={styles.cover}>
                  <img {...picture(`/catalog/setting_${world.id}.jpg`, SIZES_HALF)} alt="" />
                  <figcaption style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontWeight: 600, fontSize: 15, lineHeight: "20px" }}>
                      {capitalized(world.title_ru)}
                    </span>
                    <span className="mono muted">
                      {world.pick === "archetype" ? `типажей: ${world.archetypes.length}` : "раса и класс"}
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </ShowcaseSection>
        )}

        <ShowcaseSection title="Рядом с покупными">
          <p className="text muted">
            Рост человека — {HUMAN_HEIGHT_MM} мм от пятки до макушки, подставка — 25 мм. Встанет на поле
            рядом с фигурками из магазина.
          </p>
          <figure style={{ margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
            <div className={styles.picture} style={{ aspectRatio: "1400 / 933" }}>
              <img
                {...picture("/showcase/scale_tabletop.jpg", SIZES_FULL)}
                alt="Серая миниатюра лучницы стоит на игровом поле в ряд с покрашенными фигурками из магазина"
              />
              <span className={`${styles.centerTag} mono`}>наша · без покраски</span>
            </div>
            <figcaption className="text mono muted">
              Иллюстрация · в центре наша серая миниатюра, вокруг — фигурки из магазина
            </figcaption>
          </figure>
          <div className={styles.rows}>
            {scaleRaces(catalog).map((race) => (
              <div key={race.id} className={styles.row}>
                <span style={{ fontWeight: 600, fontSize: 15 }}>{capitalized(race.title_ru)}</span>
                <span className="mono muted" style={{ fontSize: 13 }}>
                  {raceHeightMm(race)} мм{race.id === "human" ? " · подставка 25 мм" : ""}
                </span>
              </div>
            ))}
          </div>
        </ShowcaseSection>

        <HowItWorks
          steps={[
            { title: "Сделай селфи", text: "Для большего сходства добавь фото вполоборота и в профиль." },
            { title: "Выбери мир и персонажа", text: "Или опиши своего. Позу, оружие и одежду можно попросить сразу." },
            { title: "Посмотри фигурку", text: "Эскиз будет примерно через минуту." },
          ]}
        />

        <ShowcaseSection title="Работы" aside={<span className="mono muted">лица сгенерированы</span>}>
          <div className={`${styles.gallery} ${styles.works}`}>
            {WORKS.map((work) => (
              <figure key={work.title}>
                <img {...picture(work.image, SIZES_HALF)} alt={work.title} />
                <figcaption className="small" style={{ display: "flex", flexDirection: "column" }}>
                  <span style={{ fontWeight: 600 }}>{work.title}</span>
                  <span className="muted">{work.world}</span>
                </figcaption>
              </figure>
            ))}
          </div>
          <p className="text small muted">
            Пример работы, лицо сгенерировано.
          </p>
        </ShowcaseSection>

        {prices.length > 0 && (
          <ShowcaseSection title="Цены">
            <div className={styles.rows}>
              {prices.map((row) => (
                <div key={row.product} className={styles.row}>
                  <span style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontWeight: 600 }}>{row.title}</span>
                    <span className="small muted">{row.text}</span>
                  </span>
                  <span className={styles.rowPrice}>{formatRub(row.price as number)}</span>
                </div>
              ))}
            </div>
            {cases.length > 0 && (
              <div className={styles.caseCard}>
                <div className={styles.caseThumbs}>
                  {cases.map((caseView) => (
                    <figure key={caseView.kind} className={styles.caseThumb}>
                      <span>
                        <img {...picture(caseImage(caseView.kind, "miniature"), SIZES_THIRD)} alt={caseImageAlt(caseView.kind, "miniature")} />
                      </span>
                      <figcaption>{caseView.title_ru}</figcaption>
                    </figure>
                  ))}
                </div>
                <div className={styles.casePriceRow}>
                  <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontWeight: 600 }}>Кейс для миниатюры</span>
                    <span className="small muted">{caseKindsText(cases.length)}</span>
                  </span>
                  {singleCasePrice !== null && <span className="price" style={{ fontSize: 16 }}>+ {formatRub(singleCasePrice)}</span>}
                </div>
                {casePrices.length > 1 &&
                  casePrices.map((line) => (
                    <div key={line.titles} className={styles.casePriceRow}>
                      <span className="small">{line.titles}</span>
                      <span className="price" style={{ fontSize: 15 }}>
                        + {formatRub(line.price)}
                      </span>
                    </div>
                  ))}
              </div>
            )}
          </ShowcaseSection>
        )}

        <Guarantee text="После оплаты покажем 3D-модель. Не понравится — переделаем или вернём деньги." />

        <Questions items={MINI_FAQ} allQuestionsPath={MINI_FAQ_PATH} />
        <FinalCall direction="miniature" slogan="Любой мир. Любой класс. Твоё лицо." />
      </main>
      <SiteFooter
        links={[
          { to: "/gift", label: "Фигурка в любом образе" },
          { to: FAQ_PATH, label: "Вопросы" },
          { to: "/docs", label: "Документы" },
          { to: "/docs/refund", label: "Возврат" },
        ]}
      />
    </div>
  );
}
